"use client";

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle } from "lucide-react";
import { DataTableRowActions } from "./jobs.actions";
import { DataTableColumnHeader } from "./jobs.header";

type Job = {
  id: string;
  title: string;
  description: string;
  department: string | null;
  category: string | null;
  salaryRange: string | null;
  requirements: string | null;
  responsibilities: string | null;
  qualifications: string | null;
  contactInfo: string | null;
  applicationUrl: string | null;
  status: string;
  postedDate: Date;
  expirationDate: Date | null;
  featuredImageId: string | null;
  attachmentId: string | null;
  organizationId: string;
  createdAt: Date;
  updatedAt: Date;
};

export const columns: ColumnDef<Job>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "title",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Title" />
    ),
    cell: ({ row }) => (
      <div className="max-w-[300px] font-medium">{row.getValue("title")}</div>
    ),
    enableSorting: true,
    enableHiding: true,
    filterFn: (row, _id, value) => {
      return row.original.title.toLowerCase().includes(value.toLowerCase());
    },
  },
  {
    accessorKey: "department",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Department" />
    ),
    cell: ({ row }) => {
      const department = row.original.department;
      return <div className="max-w-[150px] truncate">{department || "—"}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: "category",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Category" />
    ),
    cell: ({ row }) => {
      const category = row.original.category;
      return <div className="max-w-[150px] truncate">{category || "—"}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: "salaryRange",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Salary Range" />
    ),
    cell: ({ row }) => {
      const salaryRange = row.original.salaryRange;
      return <div className="max-w-[150px] truncate">{salaryRange || "—"}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      const variants: Record<string, "default" | "secondary" | "outline"> = {
        draft: "outline",
        active: "default",
        closed: "secondary",
      };
      return (
        <Badge variant={variants[status] || "default"} className="capitalize">
          {status}
        </Badge>
      );
    },
    enableSorting: true,
  },
  {
    accessorKey: "postedDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Posted Date" />
    ),
    cell: ({ row }) => {
      const date = row.getValue("postedDate") as Date;
      return <div>{new Date(date).toLocaleDateString()}</div>;
    },
    enableSorting: true,
  },
  {
    accessorKey: "expirationDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Expiration" />
    ),
    cell: ({ row }) => {
      const expirationDate = row.original.expirationDate;
      if (!expirationDate) return <div>—</div>;

      const isExpired = new Date(expirationDate) < new Date();
      const isExpiringSoon =
        new Date(expirationDate) <
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

      return (
        <div className="flex items-center gap-2">
          {(isExpired || isExpiringSoon) && (
            <AlertCircle
              className={`h-4 w-4 ${isExpired ? "text-destructive" : "text-yellow-500"}`}
            />
          )}
          <span className={isExpired ? "text-destructive" : ""}>
            {new Date(expirationDate).toLocaleDateString()}
          </span>
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: "actions",
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
];
